export type PocketBaseFieldLabels = Record<string, string>

type PocketBaseErrorPayload = Record<string, unknown>

function getPocketBaseErrorPayload(
  error: unknown,
): PocketBaseErrorPayload | null {
  if (!error || typeof error !== 'object') return null

  const directData = (error as { data?: unknown }).data
  if (directData && typeof directData === 'object') {
    return directData as PocketBaseErrorPayload
  }

  const response = (error as { response?: unknown }).response
  if (response && typeof response === 'object') {
    const responseData = (response as { data?: unknown }).data
    if (responseData && typeof responseData === 'object') {
      return responseData as PocketBaseErrorPayload
    }
    return response as PocketBaseErrorPayload
  }

  return error as PocketBaseErrorPayload
}

function getPocketBaseFieldData(
  payload: PocketBaseErrorPayload | null,
): PocketBaseErrorPayload | null {
  if (!payload) return null

  if (payload.data && typeof payload.data === 'object') {
    return payload.data as PocketBaseErrorPayload
  }

  if (payload.errors && typeof payload.errors === 'object') {
    return payload.errors as PocketBaseErrorPayload
  }

  const hasFieldMessages = Object.values(payload).some(
    (value) =>
      !!value &&
      typeof value === 'object' &&
      'message' in (value as Record<string, unknown>),
  )
  return hasFieldMessages ? payload : null
}

function getPocketBaseMessage(
  payload: PocketBaseErrorPayload | null,
): string | null {
  if (!payload) return null
  const message = payload.message
  if (typeof message === 'string' && message.trim().length > 0) {
    return message
  }
  return null
}

export function formatPocketBaseError(
  error: unknown,
  options?: {
    fallbackMessage?: string
    fieldLabels?: PocketBaseFieldLabels
  },
): string {
  const fallbackMessage =
    options?.fallbackMessage ?? 'Something went wrong. Please try again.'
  let baseMessage: string | null = null

  if (typeof error === 'string' && error.trim().length > 0) {
    baseMessage = error
  } else if (error instanceof Error && error.message) {
    baseMessage = error.message
  } else if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim().length > 0) {
      baseMessage = message
    }
  }

  const payload = getPocketBaseErrorPayload(error)
  if (!baseMessage) {
    baseMessage = getPocketBaseMessage(payload)
  }

  const fieldData = getPocketBaseFieldData(payload)
  const fieldMessages = fieldData
    ? Object.entries(fieldData)
        .map(([field, details]) => {
          if (!details || typeof details !== 'object') return null
          const message = (details as { message?: unknown }).message
          if (typeof message !== 'string' || message.trim().length === 0) {
            return null
          }
          const label =
            options?.fieldLabels?.[field] ?? field.replace(/_/g, ' ')
          return `${label}: ${message}`
        })
        .filter((value): value is string => Boolean(value))
    : []

  if (fieldMessages.length === 0) {
    return baseMessage ?? fallbackMessage
  }

  const header = baseMessage ?? fallbackMessage
  return `${header}\n${fieldMessages.join('\n')}`
}
